// Zipped ESRI shapefile → EUDR FeatureCollection; shpjs parses every layer in the zip, so a pre-converted
// geojson sidecar would stack a duplicate polygon on every shape unless near-identical layers are dropped.
import { roundGeometryCoordinates } from '@/utils/eudr/EudrGeometry.js'
import cloneDeep from 'lodash/cloneDeep'

// Fraction of a layer's shapes that must match another's to call the two copies of the same data.
const DUPLICATE_LAYER_THRESHOLD = 0.9

// Ring signature normalized past the .shp-vs-GeoJSON differences (start vertex, winding direction);
// assumes coordinates are already rounded to 6 decimals.
function canonicalRing (ring) {
  let points = ring
  if (points.length > 1 &&
    points[0][0] === points[points.length - 1][0] &&
    points[0][1] === points[points.length - 1][1]) {
    points = points.slice(0, -1)
  }
  const key = p => `${p[0]},${p[1]}`
  let min = 0
  for (let i = 1; i < points.length; i++) {
    if (key(points[i]) < key(points[min])) min = i
  }
  const forward = points.slice(min).concat(points.slice(0, min))
  const reverse = [forward[0], ...forward.slice(1).reverse()]
  const chosen = key(forward[1] ?? forward[0]) <= key(reverse[1] ?? reverse[0]) ? forward : reverse
  return JSON.stringify(chosen)
}

function canonicalGeometry (geometry) {
  if (geometry?.type === 'Polygon' || geometry?.type === 'MultiPolygon') {
    const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates
    return `${geometry.type}|${(polygons ?? [])
      .map(polygon => (polygon ?? []).map(canonicalRing).sort().join(';'))
      .sort()
      .join('||')}`
  }
  return `${geometry?.type}|${JSON.stringify(geometry?.coordinates ?? null)}`
}

// Polygon-only dedupe within one upload; fingerprinted on a rounded copy so uploads aren't mutated.
export function removeExactDuplicateShapes (features) {
  const seen = new Set()
  const kept = []
  let removedCount = 0
  for (const feature of features ?? []) {
    const type = feature?.geometry?.type
    if (type !== 'Polygon' && type !== 'MultiPolygon') {
      kept.push(feature)
      continue
    }
    const rounded = roundGeometryCoordinates(cloneDeep(feature.geometry))
    const key = canonicalGeometry(rounded)
    if (seen.has(key)) {
      removedCount++
      continue
    }
    seen.add(key)
    kept.push(feature)
  }
  return { features: kept, removedCount }
}

// True when enough of the smaller layer's shapes appear in the other layer to call them the same data.
function layersAreCopies (keysA, keysB) {
  if (keysA.size === 0 || keysB.size === 0) return false
  const [small, large] = keysA.size <= keysB.size ? [keysA, keysB] : [keysB, keysA]
  let matched = 0
  for (const key of small) {
    if (large.has(key)) matched++
  }
  return matched / small.size >= DUPLICATE_LAYER_THRESHOLD
}

/**
 * Converts a zipped shapefile (File/Blob) into one FeatureCollection, merging layers except duplicates.
 * @throws Error('EUDR_SHAPEFILE_DECODE') when the zip cannot be converted, Error('EUDR_SHAPEFILE_EMPTY')
 * when it converts but contains no usable features.
 * @returns {{ geojson: object, droppedLayerCount: number, droppedFeatureCount: number, duplicateShapeCount: number }}
 */
export async function buildEudrGeoJsonFromShapefile (file) {
  // Lazy import so shpjs's weight is only paid when someone actually picks a .zip.
  const { getGeojsonFromShapefile } = await import(/* webpackChunkName: "shapefile" */ '@/utils/map/Shapefiles')
  let converted = null
  try {
    converted = await getGeojsonFromShapefile(file)
  } catch (e) {
    converted = null
  }
  if (!converted) throw new Error('EUDR_SHAPEFILE_DECODE')

  const layers = (Array.isArray(converted) ? converted : [converted])
    .map(layer => (layer?.features ?? []).filter(f => f?.geometry))
    .filter(features => features.length > 0)
  if (layers.length === 0) throw new Error('EUDR_SHAPEFILE_EMPTY')

  for (const features of layers) {
    features.forEach(f => roundGeometryCoordinates(f.geometry))
  }

  // Largest layer first, so when near-copies differ slightly the most complete one is kept.
  const withKeys = layers
    .map(features => ({ features, keys: new Set(features.map(f => canonicalGeometry(f.geometry))) }))
    .sort((a, b) => b.features.length - a.features.length)

  const kept = []
  let droppedLayerCount = 0
  let droppedFeatureCount = 0
  for (const layer of withKeys) {
    if (kept.some(k => layersAreCopies(k.keys, layer.keys))) {
      droppedLayerCount++
      droppedFeatureCount += layer.features.length
      continue
    }
    kept.push(layer)
  }

  // Layer dedupe can't catch duplicates INSIDE one layer, so a feature-level pass follows.
  const { features, removedCount: duplicateShapeCount } = removeExactDuplicateShapes(kept.flatMap(layer => layer.features))
  return {
    geojson: { type: 'FeatureCollection', features },
    droppedLayerCount,
    droppedFeatureCount,
    duplicateShapeCount
  }
}
